import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, School, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import MessageField from "@/components/messages/MessageField";
import MessageHtml from "@/components/messages/MessageHtml";
import { api, errorMessage, post } from "@/lib/api";
import { messagePlain, messageReady } from "@/lib/messageBody";

export type ParentSchool = {
  id: string;
  name: string | null;
  description: string | null;
  logoUrl: string | null;
  detailsAvailable: boolean;
  children: Array<{ studentUserId: string; studentName: string }>;
};

type OfficeEntry = {
  id: string;
  body: string;
  authorName: string | null;
  authorRole: string;
  at: string;
};

type OfficeSummary = {
  id: string;
  title: string;
  lastBody: string;
  lastAt: string;
};

type OfficeThread = OfficeSummary & {
  entries: OfficeEntry[];
};

function whenLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function schoolName(school: ParentSchool) {
  return school.name || "Your student’s school";
}

function childrenLine(school: ParentSchool) {
  return school.children.map((child) => child.studentName).join(", ");
}

function useParentSchools() {
  const [schools, setSchools] = useState<ParentSchool[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    api<{ success: boolean; data: ParentSchool[] }>("/api/parent/schools")
      .then((env) => {
        if (!cancel) setSchools(env.data || []);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, []);

  return { schools, error };
}

function SchoolMark({ school }: { school: ParentSchool }) {
  return school.logoUrl ? (
    <img className="parent-school-mark" src={school.logoUrl} alt="" />
  ) : (
    <span className="parent-school-mark" aria-hidden>
      <School size={18} />
    </span>
  );
}

export function ParentSchools() {
  const { schools, error } = useParentSchools();

  return (
    <div className="student-page student-page--parent">
      <div className="student-page-head">
        <span className="student-eyebrow">Schools</span>
        <h1>Your student’s schools</h1>
        <p>The schools your students attend, and who is at each one.</p>
      </div>
      {error ? (
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      ) : !schools ? (
        <div aria-busy="true" aria-live="polite">
          <span className="student-vh">Loading schools</span>
          <div className="student-ghost-card">
            <div className="student-ghost student-ghost--line student-ghost--w70" />
            <div className="student-ghost student-ghost--meta" />
            <div className="student-ghost student-ghost--line student-ghost--w60" />
          </div>
        </div>
      ) : schools.length === 0 ? (
        <div className="student-empty">
          <div>
            <strong>No schools yet</strong>
            <p>When your student is placed in a class, their school shows up here.</p>
          </div>
        </div>
      ) : (
        <section className="student-card">
          {schools.map((school) => (
            <Link key={school.id} className="student-row parent-school-row" to={`/parent/schools/${encodeURIComponent(school.id)}`}>
              <SchoolMark school={school} />
              <span className="parent-school-row__main">
                <strong>{schoolName(school)}</strong>
                {school.description ? (
                  <span className="parent-school-row__desc">{school.description}</span>
                ) : null}
                <span className="student-row__sub">{childrenLine(school)}</span>
              </span>
              <ChevronRight size={16} className="parent-school-row__chev" aria-hidden />
            </Link>
          ))}
        </section>
      )}
    </div>
  );
}

export function ContactSchoolForm({
  school,
  schools,
  onClose,
}: {
  school?: ParentSchool | null;
  schools?: ParentSchool[];
  onClose: () => void;
}) {
  const { capabilities } = useAuth();
  const canOffice = Boolean(capabilities?.messageStudentsOffice);
  const canSend = canOffice;
  const canReply = canOffice;
  const options = (schools && schools.length ? schools : school ? [school] : []).filter(Boolean);
  const [schoolId, setSchoolId] = useState(options[0]?.id || "");
  const current = options.find((row) => row.id === schoolId) || options[0] || null;
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [threads, setThreads] = useState<OfficeSummary[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [thread, setThread] = useState<OfficeThread | null>(null);
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!current?.id) return undefined;
    let cancel = false;
    setThreads(null);
    setOpenId(null);
    setThread(null);
    setComposing(false);
    api<{ success: boolean; data: OfficeSummary[] }>(
      `/api/parent/schools/${encodeURIComponent(current.id)}/messages`,
    )
      .then((env) => {
        if (!cancel) setThreads(env.data || []);
      })
      .catch(() => {
        if (!cancel) setThreads([]);
      });
    return () => {
      cancel = true;
    };
  }, [current?.id]);

  useEffect(() => {
    if (!current?.id || !openId) {
      setThread(null);
      return undefined;
    }
    let cancel = false;
    api<{ success: boolean; data: OfficeThread }>(
      `/api/parent/schools/${encodeURIComponent(current.id)}/messages/${openId}`,
    )
      .then((env) => {
        if (!cancel) setThread(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [current?.id, openId]);

  const sendNew = async (event: FormEvent) => {
    event.preventDefault();
    if (!current || busy) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: OfficeThread }>(
        `/api/parent/schools/${encodeURIComponent(current.id)}/messages`,
        { title, body: message },
      );
      setThreads((rows) => [
        {
          id: env.data.id,
          title: env.data.title,
          lastBody: messagePlain(message),
          lastAt: env.data.entries[env.data.entries.length - 1]?.at || new Date().toISOString(),
        },
        ...(rows || []).filter((row) => row.id !== env.data.id),
      ]);
      setThread(env.data);
      setOpenId(env.data.id);
      setComposing(false);
      setTitle("");
      setMessage("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    if (!current || !openId || busy) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: OfficeThread }>(
        `/api/parent/schools/${encodeURIComponent(current.id)}/messages/${openId}/replies`,
        { body: message },
      );
      setThread(env.data);
      const last = env.data.entries[env.data.entries.length - 1];
      setThreads((rows) =>
        (rows || [])
          .map((row) =>
            row.id === env.data.id
              ? { ...row, lastBody: messagePlain(last?.body || row.lastBody), lastAt: last?.at || row.lastAt }
              : row,
          )
          .sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime()),
      );
      setMessage("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="student-card parent-form-card">
      <div className="student-section__head parent-announcements__head">
        <h2 className="parent-card-title">Contact the school office</h2>
        <button type="button" className="student-text-link" onClick={onClose}>
          Close
        </button>
      </div>
      {options.length > 1 ? (
        <label className="parent-field" htmlFor="parent-contact-school">
          <span>School</span>
          <select
            id="parent-contact-school"
            className="student-input"
            value={current?.id || ""}
            onChange={(e) => setSchoolId(e.target.value)}
          >
            {options.map((row) => (
              <option key={row.id} value={row.id}>
                {schoolName(row)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <p className="student-row__sub parent-form-lede">
        {current
          ? `Each note to ${schoolName(current)} is its own conversation. Start a new one when it is about something else.`
          : "There is no school office to contact yet."}
      </p>
      {openId ? (
        <div className="parent-office-sent">
          <button
            type="button"
            className="student-text-link"
            onClick={() => {
              setOpenId(null);
              setMessage("");
              setError("");
            }}
          >
            All messages
          </button>
          {thread ? (
            <>
              <p className="student-row__sub">{thread.title || "Conversation"}</p>
              {thread.entries.map((entry) => (
                <div key={entry.id} className="parent-note">
                  <p>{entry.authorRole === "instructor" ? entry.authorName || "Office" : "You"}</p>
                  <MessageHtml body={entry.body} />
                </div>
              ))}
            </>
          ) : (
            <div aria-busy="true" aria-live="polite">
              <span className="student-vh">Loading conversation</span>
              <div className="student-ghost-card">
                <div className="student-ghost student-ghost--line student-ghost--w80" />
                <div className="student-ghost student-ghost--line student-ghost--w90" />
                <div className="student-ghost student-ghost--meta" />
              </div>
            </div>
          )}
          {canReply ? (
          <form className="parent-form" onSubmit={(event) => void sendReply(event)}>
            <MessageField
              id="parent-contact-body"
              label="Reply"
              value={message}
              onChange={setMessage}
              placeholder="Write a reply"
              disabled={busy}
            />
            {error ? <p className="parent-form-error">{error}</p> : null}
            <div className="parent-form-actions">
              <button type="submit" className="student-btn" disabled={busy || !messageReady(message)}>
                <Send size={15} aria-hidden /> {busy ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
          ) : null}
        </div>
      ) : (
        <>
          {threads && threads.length > 0 ? (
            <ul className="parent-office-list">
              {threads.map((row) => (
                <li key={row.id}>
                  <button type="button" className="parent-office-open" onClick={() => setOpenId(row.id)}>
                    <strong>{row.title || "Conversation"}</strong>
                    <span>
                      {messagePlain(row.lastBody)}
                      {row.lastAt ? ` · ${whenLabel(row.lastAt)}` : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {canSend && threads && threads.length > 0 && !composing ? (
            <button type="button" className="student-btn student-btn--ghost" onClick={() => setComposing(true)}>
              New message
            </button>
          ) : null}
          {canSend && current && (composing || (threads && threads.length === 0)) ? (
            <form className="parent-form" onSubmit={(event) => void sendNew(event)}>
              <label className="parent-field" htmlFor="parent-contact-title">
                <span>Title</span>
                <input
                  id="parent-contact-title"
                  className="student-input"
                  value={title}
                  maxLength={200}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What is this about?"
                />
              </label>
              <MessageField
                id="parent-contact-message"
                label="Message"
                value={message}
                onChange={setMessage}
                placeholder="Write your message"
                disabled={busy}
              />
              {error ? <p className="parent-form-error">{error}</p> : null}
              <div className="parent-form-actions">
                {threads && threads.length > 0 ? (
                  <button
                    type="button"
                    className="student-text-link"
                    onClick={() => {
                      setComposing(false);
                      setTitle("");
                      setMessage("");
                      setError("");
                    }}
                  >
                    Cancel
                  </button>
                ) : null}
                <button type="submit" className="student-btn" disabled={busy || !title.trim() || !messageReady(message)}>
                  <Send size={15} aria-hidden /> {busy ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          ) : null}
        </>
      )}
    </section>
  );
}

export function ParentSchoolDetail() {
  const { schoolId = "" } = useParams();
  const { capabilities } = useAuth();
  const canOffice = Boolean(capabilities?.messageStudentsOffice);
  const { schools, error } = useParentSchools();
  const [contactOpen, setContactOpen] = useState(false);
  const school = schools?.find((row) => row.id === schoolId) ?? null;

  const back = (
    <Link className="student-text-link" to="/parent/schools">
      <ChevronLeft size={15} /> Schools
    </Link>
  );

  if (error || (schools && !school)) {
    return (
      <div className="student-page student-page--parent">
        {back}
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error || "That school was not found."}
        </div>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="student-page student-page--parent" aria-busy="true" aria-live="polite">
        <span className="student-vh">Loading this school</span>
        <div className="student-ghost student-ghost--link" />
        <div className="student-ghost-head">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--title" />
        </div>
        <div className="student-ghost-card">
          <div className="student-ghost student-ghost--line student-ghost--w90" />
          <div className="student-ghost student-ghost--line student-ghost--w70" />
          <div className="student-ghost student-ghost--meta" />
        </div>
      </div>
    );
  }

  return (
    <div className="student-page student-page--parent">
      {back}
      <div className="student-page-head parent-school-head" style={{ marginTop: 10 }}>
        <SchoolMark school={school} />
        <div>
          <span className="student-eyebrow">School</span>
          <h1>{schoolName(school)}</h1>
          {school.description ? <p>{school.description}</p> : null}
          {!school.detailsAvailable ? (
            <p>More about this school is not available yet.</p>
          ) : null}
        </div>
      </div>

      <div className="parent-overview">
        <section className="student-card">
          <h2 className="parent-card-title">Your students here</h2>
          {school.children.map((child) => (
            <Link
              key={child.studentUserId}
              className="student-row"
              to={`/parent/student/${encodeURIComponent(child.studentUserId)}`}
            >
              <span>
                <strong>{child.studentName}</strong>
              </span>
              <ChevronRight size={16} aria-hidden />
            </Link>
          ))}
        </section>

        {canOffice && contactOpen ? (
          <ContactSchoolForm school={school} onClose={() => setContactOpen(false)} />
        ) : canOffice ? (
          <div className="parent-contact-open">
            <button
              type="button"
              className="student-btn student-btn--ghost student-btn--sm"
              onClick={() => setContactOpen(true)}
            >
              Contact School
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
